/**
 * Authentication Service
 * 
 * This file contains authentication-related API calls and utilities.
 * It handles login, logout, password reset, and token management.
 */

import { apiService } from './api';
import config from '../config/environment';
import { logger } from '../utils/logger';

/**
 * Authentication API calls
 */
export const authApi = {
  /**
   * Login user
   * @param {object} credentials - Username and password
   * @param {string} credentials.username - User's username
   * @param {string} credentials.password - User's password
   * @param {boolean} credentials.rememberMe - Remember login state
   * @returns {Promise} - Login response promise
   */
  login: async (credentials) => {
    logger.info('🔐 [AUTH] Login function called');
    logger.info('📝 [AUTH] Login credentials received:', {
      username: credentials.username,
      password: credentials.password ? '***HIDDEN***' : 'NOT_PROVIDED',
      rememberMe: credentials.rememberMe || false,
      timestamp: new Date().toISOString()
    });

    try {
      logger.info('🚀 [AUTH] Initiating API call to /auth/login');
      logger.info('📤 [AUTH] Request payload:', {
        username: credentials.username,
        password: '***HIDDEN***',
        endpoint: '/auth/login'
      });

      const response = await apiService.post('/auth/login', {
        username: credentials.username,
        password: credentials.password,
        //rememberMe: credentials.rememberMe || false,
      });
      
      logger.info('✅ [AUTH] Login API call successful');
      logger.info('📥 [AUTH] Response received:', {
        status: response.status,
        statusText: response.statusText,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        timestamp: new Date().toISOString()
      });

      if (response.data) {
        logger.info('🎯 [AUTH] Login response data structure:', {
          hasToken: !!response.data.token,
          hasUser: !!response.data.user,
          hasRefreshToken: !!response.data.refreshToken,
          tokenLength: response.data.token ? response.data.token.length : 0
        });
      }

      logger.info('🏁 [AUTH] Login function completed successfully');
      return response.data;
    } catch (error) {
      logger.error('❌ [AUTH] Login failed with error');
      logger.error('🔍 [AUTH] Error details:', {
        message: error.detail,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        code: error.code,
        timestamp: new Date().toISOString()
      });

      if (error.response) {
        logger.error('📡 [AUTH] Server response error:', {
          status: error.response.status,
          headers: error.response.headers,
          data: error.response.data
        });
      } else if (error.request) {
        logger.error('🌐 [AUTH] Network error - no response received:', error.request);
      } else {
        logger.error('⚙️ [AUTH] Request setup error:', error.detail);
      }

      logger.info('🔄 [AUTH] Rethrowing error for upstream handling');
      throw error;
    }
  },

  /**
   * Logout user
   * @returns {Promise} - Logout response promise
   */
  logout: async () => {
    try {
     
      await apiService.post('/auth/logout');
      
      // Clear local storage
      authUtils.clearStoredToken();
      console.log('local storage cleared');
      // Clear cookies (if any)
      document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      window.location.href = 'https://ikmpt.dsrs.illinois.edu/oauth2/sign_out?rd=https://ikompete.dsrs.illinois.edu/login';
      console.log('User logged out successfully');
      return { success: true };
    } catch (error) {
      // Even if API call fails, clear local storage
      authUtils.clearStoredToken();
      document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      window.location.href = 'https://ikmpt.dsrs.illinois.edu/oauth2/sign_out?rd=https://ikompete.dsrs.illinois.edu/login';

      throw error;
    }
  },

  /**
   * Request password reset
   * @param {string} email - User's email address
   * @returns {Promise} - Password reset response promise
   */
  forgotPassword: async (email) => {
    try {
      const response = await apiService.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Reset password with token
   * @param {object} resetData - Reset password data
   * @param {string} resetData.token - Reset token from email
   * @param {string} resetData.password - New password
   * @param {string} resetData.confirmPassword - Confirm new password
   * @returns {Promise} - Reset password response promise
   */
  resetPassword: async (resetData) => {
    try {
      const response = await apiService.post('/auth/reset-password', {
        token: resetData.token,
        password: resetData.password,
        confirmPassword: resetData.confirmPassword,
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Refresh authentication token
   * @param {string} refreshToken - Refresh token
   * @returns {Promise} - Refresh token response promise
   */
  refreshToken: async (refreshToken) => {
    try {
      const response = await apiService.post('/auth/refresh', { refreshToken });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get current user profile
   * @returns {Promise} - User profile response promise
   */
  getCurrentUser: async () => {
    try {
      // Single profile request: interceptor adds Authorization when token exists,
      // and cookies are included for SSO sessions.
      const response = await apiService.post('/auth/profile');
      if (response?.data?.token) {
        const hasSessionToken = !!sessionStorage.getItem(config.TOKEN_STORAGE_KEY);
        authUtils.storeToken(response.data.token, !hasSessionToken);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

/**
 * Authentication utilities
 */
export const authUtils = {
  /**
   * Validate login credentials
   * @param {object} credentials - Username and password
   * @returns {object} - Validation result
   */
  validateLoginCredentials: (credentials) => {
    const errors = {};
    
    if (!credentials.username || credentials.username.trim() === '') {
      errors.username = 'Username is required';
    }
    
    if (!credentials.password || credentials.password.trim() === '') {
      errors.password = 'Password is required';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  /**
   * Validate password reset data
   * @param {object} resetData - Reset password data
   * @returns {object} - Validation result
   */
  validateResetPassword: (resetData) => {
    const errors = {};
    
    if (!resetData.password || resetData.password.trim() === '') {
      errors.password = 'Password is required';
    } else if (resetData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }
    
    if (!resetData.confirmPassword || resetData.confirmPassword.trim() === '') {
      errors.confirmPassword = 'Please confirm your password';
    } else if (resetData.password !== resetData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  /**
   * Validate email format
   * @param {string} email - Email address
   * @returns {boolean} - True if valid email format
   */
  validateEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Check if session is expired
   * @returns {boolean} - True if session is expired
   */
  isSessionExpired: () => {
    const token = authUtils.getStoredToken();
    if (!token) return true;
    
    try {
      // Decode JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      return payload.exp < currentTime;
    } catch (error) {
      // If token is malformed, consider it expired
      return true;
    }
  },

  /**
   * Get user role from token
   * @returns {string|null} - User role or null
   */
  getUserRole: () => {
    const token = authUtils.getStoredToken();
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role || null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Check if user has specific role
   * @param {string} requiredRole - Required role
   * @returns {boolean} - True if user has the role
   */
  hasRole: (requiredRole) => {
    const userRole = authUtils.getUserRole();
    return userRole === requiredRole;
  },

  /**
   * Format authentication error message
   * @param {object} error - Error object
   * @returns {string} - Formatted error message
   */
  formatAuthError: (error) => {
    if (error.response && error.response.data && error.response.data.message) {
      return error.response.data.message;
    }
    switch (error.code) {
      case 'INVALID_CREDENTIALS':
        return 'Invalid username or password. Please try again.';
      case 'ACCOUNT_LOCKED':
        return 'Your account has been locked. Please contact support.';
      case 'ACCOUNT_DISABLED':
        return 'Your account has been disabled. Please contact support.';
      case 'PASSWORD_EXPIRED':
        return 'Your password has expired. Please reset your password.';
      case 'NETWORK_ERROR':
        return 'Unable to connect to the server. Please check your internet connection.';
      default:
        return error.message || 'An authentication error occurred. Please try again.';
    }
  },

  /**
   * Get stored authentication token
   * @returns {string|null} - Stored token or null
   */
  getStoredToken: () => {
    return localStorage.getItem(config.TOKEN_STORAGE_KEY) || sessionStorage.getItem(config.TOKEN_STORAGE_KEY);
  },

  /**
   * Store authentication token
   * @param {string} token - JWT token
   * @param {boolean} remember - Whether to remember login (use localStorage)
   */
  storeToken: (token, remember = false) => {
    if (remember) {
      localStorage.setItem(config.TOKEN_STORAGE_KEY, token);
      sessionStorage.removeItem(config.TOKEN_STORAGE_KEY);
    } else {
      sessionStorage.setItem(config.TOKEN_STORAGE_KEY, token);
      localStorage.removeItem(config.TOKEN_STORAGE_KEY);
    }
  },

  /**
   * Clear stored authentication token
   */
  clearStoredToken: () => {
    localStorage.removeItem(config.TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(config.TOKEN_STORAGE_KEY);
  },
};

/**
 * Session management
 */
export const sessionManager = {
  /**
   * Start session timeout timer
   */
  startSessionTimer: () => {
    // Clear existing timer
    if (window.sessionTimer) {
      clearTimeout(window.sessionTimer);
    }
    
    // Set new timer
    window.sessionTimer = setTimeout(() => {
      authApi.logout();
      window.location.href = '/login?reason=session_expired';
    }, config.SESSION_TIMEOUT);
  },

  /**
   * Reset session timeout timer
   */
  resetSessionTimer: () => {
    if (authUtils.getStoredToken() && !authUtils.isSessionExpired()) {
      sessionManager.startSessionTimer();
    }
  },

  /**
   * Clear session timeout timer
   */
  clearSessionTimer: () => {
    if (window.sessionTimer) {
      clearTimeout(window.sessionTimer);
      window.sessionTimer = null;
    }
  },
};

export default authApi;
